import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { resolveProductAttributes } from '@/lib/productAttributes';

/**
 * Etiquetas/iconos del producto con tooltip (hover) y tap (móvil).
 * Por defecto muestra icono + nombre para que se lean en carta y panel.
 */
export function ProductAttributeIcons({
  attributes,
  className,
  iconClassName,
  size = 'md',
  stopPropagation = false,
  showLabel = true,
}) {
  const items = resolveProductAttributes(attributes);
  if (items.length === 0) return null;

  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn('flex flex-wrap items-center gap-1.5', className)}
        data-testid="product-attribute-icons"
      >
        {items.map((attr) => (
          <AttributeIconButton
            key={attr.id}
            attr={attr}
            iconSize={iconSize}
            size={size}
            showLabel={showLabel}
            iconClassName={iconClassName}
            stopPropagation={stopPropagation}
          />
        ))}
      </div>
    </TooltipProvider>
  );
}

function AttributeIconButton({
  attr,
  iconSize,
  size,
  showLabel,
  iconClassName,
  stopPropagation,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const Icon = attr.Icon;
  const label = t(`attributes.${attr.id}`, { defaultValue: attr.label });

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          title={label}
          className={cn(
            'inline-flex items-center justify-center gap-1 rounded-full border border-border/70 bg-background/90 text-foreground/90',
            'transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            showLabel
              ? size === 'sm'
                ? 'h-7 px-2 text-[11px] font-medium'
                : 'h-8 px-2.5 text-xs font-medium'
              : size === 'sm'
                ? 'h-7 w-7'
                : 'h-8 w-8',
          )}
          onClick={(e) => {
            if (stopPropagation) {
              e.preventDefault();
              e.stopPropagation();
            }
            setOpen((v) => !v);
          }}
          onPointerDown={(e) => {
            if (stopPropagation) e.stopPropagation();
          }}
        >
          <Icon className={cn(iconSize, 'shrink-0', iconClassName)} aria-hidden />
          {showLabel ? <span>{label}</span> : null}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[200px] text-center">
        <p className="font-medium">{label}</p>
        {attr.description ? (
          <p className="mt-0.5 text-[11px] text-primary-foreground/80">{attr.description}</p>
        ) : null}
      </TooltipContent>
    </Tooltip>
  );
}
